import React from 'react';
import { formatCivilDate } from '../helpers/dataHelper';

export interface BaseViewerProps {
    content?: string;
    fileName?: string;
    className?: string;
}

export interface ViewerSectionProps {
    title: string;
    children?: React.ReactNode;
    roundedClass?: string;
    className?: string;
}

export interface ViewerPillProps {
    children: React.ReactNode;
    className?: string;
}

export interface ViewerKeyValProps {
    label: string;
    value: any;
    className?: string;
}

// Common Section component used across viewers
export const ViewerSection: React.FC<ViewerSectionProps> = ({ 
    title, 
    children, 
    roundedClass = 'rounded-xl',
    className = '' 
}) => (
    <div className={`mb-4 border border-hex-gray-300 bg-hex-white ${roundedClass} ${className}`}>
        <div className={`px-3 py-2 border-b border-hex-gray-300 font-semibold text-sm text-hex-gray-700 bg-hex-gray-50 ${
            roundedClass ? roundedClass.replace('rounded', 'rounded-t') : 'rounded-t-xl'
        }`}>
            {title}
        </div>
        <div className="p-3 text-sm text-hex-gray-700">{children}</div>
    </div>
);

// Common Pill component used across viewers
export const ViewerPill: React.FC<ViewerPillProps> = ({ 
    children, 
    className = '' 
}) => (
            <span className={`inline-block bg-hex-gray-50 border border-hex-gray-300 rounded px-2 py-0.5 leading-5 text-text-xs text-hex-gray-700 ${className}`}>
        {children}
    </span>
);

// Common KeyVal component used across viewers
export const ViewerKeyVal: React.FC<ViewerKeyValProps> = ({ 
    label, 
    value, 
    className = '' 
}) => (
    <div className={`grid grid-cols-[220px_minmax(0,1fr)] gap-2 items-center py-1 ${className}`}>
        <div className="text-text-xs font-semibold text-hex-gray-700">{label}</div>
        <div className="flex flex-wrap gap-2">
            {Array.isArray(value)
                ? (value.length ? value.map((v, i) => <ViewerPill key={i}>{String(v)}</ViewerPill>) : <ViewerPill>-</ViewerPill>)
                : <ViewerPill>{String(value ?? '-')}</ViewerPill>}
        </div>
    </div>
);

// Common utility functions
export const formatCurrency = (v: any): string => {
    const n = Number(v);
    if (!isFinite(n)) return v ?? "-";
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

/** Compact YYYYMMDD or other civil date strings — UTC calendar day, no local TZ shift. */
export const formatDateYmd = (ymd?: string): string => {
    if (!ymd) return '-';
    return formatCivilDate(ymd);
};

// Base viewer component that can be extended
const BaseViewer: React.FC<BaseViewerProps> = ({ 
    content, 
    fileName, 
    className = '' 
}) => {
    return (
        <div className={`flex flex-col gap-4 ${className}`}>
            {/* Override this in child components */}
            <div className="text-sm text-hex-gray-500">
                Base viewer - extend this component to implement specific functionality
            </div>
        </div>
    );
};

export default BaseViewer;
